import { Stack, Text, Center } from '@mantine/core';
import { Database } from 'lucide-react';
import { SessionFeedbackToolbar } from './SessionFeedbackToolbar';
import { SessionFeedbackTable } from './SessionFeedbackTable';
import { useDataLibrarySessionFeedback } from './useDataLibrarySessionFeedback';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';

interface DataLibrarySessionFeedbackTabProps {
  selectedCohortIds: string[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

export function DataLibrarySessionFeedbackTab({
  selectedCohortIds,
  activeGroup,
  onGroupChange,
}: DataLibrarySessionFeedbackTabProps) {
  const {
    rows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibrarySessionFeedback({ selectedCohortIds, activeGroup, onGroupChange });

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Database size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view session feedback
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  const emptyMessage = !hasSurveyConfig
    ? 'No post-session survey configured for this cohort'
    : 'No session feedback responses yet';

  return (
    <Stack gap="sm">
      <SessionFeedbackToolbar
        groupLimitTotal={groupLimitTotal}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        filterOptions={filterOptions}
        activeQuickFilters={activeQuickFilters}
        onQuickFilterToggle={handleQuickFilterToggle}
        activeFilters={activeFilters}
        onFilterAdd={handleFilterAdd}
        onFilterChange={handleFilterChange}
        onFilterRemove={handleFilterRemove}
        onClearAll={clearAllFilters}
        hasActiveFilters={hasActiveFilters}
        sortConfig={sortConfig}
        onSortChange={handleSortPanelChange}
        activeGroup={activeGroup}
        onGroupChange={onGroupChange}
        columns={columns}
        onColumnsChange={setColumns}
        totalCount={totalCount}
        selectedCohortIds={selectedCohortIds}
      />

      <SessionFeedbackTable
        data={rows}
        averages={averages}
        groupedData={groupedRows}
        columns={visibleColumns}
        questions={questions}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters && hasSurveyConfig}
        onClearFilters={clearAllFilters}
        emptyMessage={emptyMessage}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hidePagination={!!groupedRows}
      />
    </Stack>
  );
}
